import { useState } from "react";
import {
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  type TypographyProps,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";
import classNames from "classnames";
import classes from "../CMSHomepagePersonalization.module.less";
import { C_SURFACE } from "~/common/components/icon/Colors";
import { DateFormatType, formatNullableDate } from "~/utils/DateFormatter";
import * as PriceUtils from "../../../../utils/PriceUtils";
import type { OrderData } from "~/OrdersQuotes/common/modelsV2";
import { Link } from "~/common/components/Link";
import Tag from "~/common/components/Tag/Tag";
import { lineClamp } from "~/common/components/icon/FontUtils";

export enum TableType {
  ORDERS = "ORDERS",
  QUOTES = "QUOTES",
}
interface OrderTableProps {
  type: TableType.ORDERS | TableType.QUOTES;
  data: OrderData[]; // new prop
}

export default function OrderTable({ type, data }: OrderTableProps) {
  const { t } = useTranslation();
  const rowsPerPage = 5;
  const [page, setPage] = useState(0);

  // Calculate total pages
  const totalPages = Math.ceil(data.length / rowsPerPage);

  // Slice data to display only current page rows
  const currentRows = data.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  if (data.length == 0) {
    return null;
  }

  return (
    <>
      <Table sx={{ tableLayout: "fixed" }}>
        <TableHead>
          <TableRow>
            <HeaderCell>
              <Typography variant="labelMedium">
                {type === TableType.QUOTES
                  ? t("quoteListPage.table.header.quoteNumber")
                  : t("homepage.recentOrderTH.poNumber")}
              </Typography>
            </HeaderCell>
            <HeaderCell>
              <TruncatedTypography variant="labelMedium">
                {type === TableType.QUOTES
                  ? t("quoteListPage.table.header.customerReferences")
                  : t("homepage.recentOrderTH.soNumber")}
              </TruncatedTypography>
            </HeaderCell>
            <HeaderCell sx={{ width: "114px" }}>
              <Typography variant="labelMedium">{t("homepage.recentOrderTH.dateEntered")}</Typography>
            </HeaderCell>
            <HeaderCell sx={{ width: "136px" }}>
              <Typography variant="labelMedium">
                {type === TableType.QUOTES ? t("homepage.recentOrderTH.status") : t("homepage.recentOrderTH.netPrice")}
              </Typography>
            </HeaderCell>
            <HeaderCell sx={{ width: "40px" }}></HeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {currentRows.map((item, index) => (
            <StyledTableRow key={index}>
              <StyledCell>
                <TruncatedTypography component={"b"}>{item.poNumber}</TruncatedTypography>
              </StyledCell>
              <StyledCell>
                <TruncatedTypography>{item.soNumber}</TruncatedTypography>
              </StyledCell>
              <StyledCell>
                <Typography>{formatNullableDate(item.datePlaced, DateFormatType.DATE)}</Typography>
              </StyledCell>
              <StyledCell>
                {type === TableType.QUOTES ? (
                  <Tag className={classes.status}>{t(item.orderStatus)}</Tag>
                ) : (
                  <Typography component={"b"}>{PriceUtils.text(item.totalPrice)}</Typography>
                )}
              </StyledCell>
              <StyledCell>
                {!item.soNumber || item.soNumber == "-" ? (
                  <Tooltip placement="top" title={t("homepage.recentOrderTable.tooltip")} variant="simple">
                    <ArrowForwardIcon color="disabled" />
                  </Tooltip>
                ) : (
                  <Link href={item.detailsUrl}>
                    <ArrowForwardIcon color="primary" />
                  </Link>
                )}
              </StyledCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>

      <Stack direction="row" justifyContent="center" gap={1} marginY={1}>
        {Array.from({ length: totalPages }).map((_, idx) => (
          <div
            key={`page-control-${idx}`}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setPage(idx);
            }}
            className={classNames(classes.paginationItem, idx === page && classes.paginationItemActive)}
          />
        ))}
      </Stack>
    </>
  );
}

const StyledTableRow = styled(TableRow)({
  backgroundColor: `white !important`,
  transition: "background-color 0.3s ease-in-out",
  "&:hover": {
    backgroundColor: `${C_SURFACE} !important`,
    cursor: "pointer",
  },
});

const StyledCell = styled(TableCell)({
  verticalAlign: "middle !important",
});

const HeaderCell = styled(StyledCell)({
  padding: "8px !important",
  "> label": {
    ...getFontStyle(FontSize.LabelMedium),
    fontWeight: "bold",
  },
});

const TruncatedTypography = styled(Typography)<TypographyProps>({
  ...lineClamp(1),
});
